// src/pages/AccountPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, User, Wine, Shield, Loader, X, Plus } from "lucide-react";
import { TopBar, ErrorMsg } from "../components/UI";
import { useAuth } from "../context/AuthContext";
import { api } from "../services/api";

const ROLE_LABEL = {
  traveler:     { label: "Traveler",     color: "#886070", bg: "rgba(107,39,55,0.08)" },
  winery_owner: { label: "Winery Owner", color: "#7A6A55", bg: "rgba(122,106,85,0.1)" },
  road_rep:     { label: "Road Rep",     color: "#4A7052", bg: "rgba(74,112,82,0.1)"  },
  admin:        { label: "Admin",        color: "#2E3D48", bg: "#EAEFE2" },
};

function Toggle({ checked, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} style={{
      position: "relative", width: 38, height: 21, borderRadius: 11, flexShrink: 0,
      background: checked ? "#2E5235" : "#E8EDE0",
      border: `1px solid ${checked ? "#3A6A42" : "#B8C8A0"}`,
      cursor: "pointer", transition: "all 0.2s",
    }}>
      <span style={{
        position: "absolute", top: 1, left: 1, width: 15, height: 15, borderRadius: "50%",
        background: checked ? "#6AAA7A" : "#2E3D48",
        transition: "transform 0.2s", transform: checked ? "translateX(17px)" : "translateX(0)",
        display: "block",
      }} />
    </button>
  );
}

function BookingsSummaryCard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.myBookings()
      .then(bookings => {
        if (cancelled) return;
        const today = new Date(); today.setHours(0, 0, 0, 0);
        setStats({
          total: bookings.length,
          pending: bookings.filter(b => b.status === "pending").length,
          confirmed: bookings.filter(b => b.status === "approved").length,
          outdated: bookings.filter(b => new Date(b.visit_date) < today).length,
        });
      })
      .catch(() => { if (!cancelled) setStats({ total: 0, pending: 0, confirmed: 0, outdated: 0 }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const stat = { fontSize: 18, fontWeight: 700, color: "#FFFFFF", fontFamily: "'Playfair Display', serif" };
  const statLabel = { fontSize: 10, color: "rgba(255,255,255,0.75)", textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 2 };

  return (
    <button onClick={() => navigate("/bookings")} style={{
      display: "block", width: "100%", textAlign: "center", cursor: "pointer",
      background: "var(--burgundy, #8B1A2F)", border: "none", borderRadius: 10,
      padding: "18px 16px", transition: "background 0.2s",
    }}>
      <p style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, fontSize: 17, color: "#FFFFFF", margin: 0 }}>
        Manage bookings
      </p>

      {loading ? (
        <div style={{ height: 40, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Loader size={16} color="rgba(255,255,255,0.8)" />
        </div>
      ) : (
        <div style={{ display: "flex", justifyContent: "space-around", marginTop: 14 }}>
          <div><div style={stat}>{stats.total}</div><div style={statLabel}>Total</div></div>
          <div><div style={stat}>{stats.pending}</div><div style={statLabel}>Pending</div></div>
          <div><div style={stat}>{stats.confirmed}</div><div style={statLabel}>Confirmed</div></div>
          <div><div style={stat}>{stats.outdated}</div><div style={statLabel}>Outdated</div></div>
        </div>
      )}

      <p style={{ fontSize: 11, color: "rgba(255,255,255,0.75)", marginTop: 14, marginBottom: 0 }}>
        Click to manage your bookings
      </p>
    </button>
  );
}

const ROAD_OPTIONS = [
  { value: "", label: "No default" },
  { value: "ar-valley", label: "Ahr Valley" },
  { value: "vienna-venice", label: "Vienna – Venice" },
  { value: "malopolska", label: "Małopolska" },
];
const OBJECTIVE_OPTIONS = [
  { value: "", label: "---" },
  { value: "min_time", label: "Fastest route" },
  { value: "max_rating", label: "Highest rated" },
  { value: "max_feedback", label: "Most reviewed" },
];
const BUDGET_OPTIONS = [
  { value: "", label: "---" },
  { value: 1, label: "Budget" },
  { value: 2, label: "Moderate" },
  { value: 3, label: "Premium" },
  { value: 4, label: "Luxury" },
];

function TravelerSettings({ user, onSaved }) {
  const [prefs, setPrefs] = useState({
    notify_on_plan_completion: user.notify_on_plan_completion !== false,
    notify_on_booking_status: user.notify_on_booking_status !== false,
    interaction_method: user.interaction_method || "form",
    favorite_wines: user.favorite_wines || [],
  });
  // Reflects ONLY what's actually saved — no hardcoded defaults merged in.
  // Unset fields show "---" and are not sent to the backend until the user
  // actually picks a value. predefined_plan_criteria is a full replace on
  // the backend (not a merge), so if we pre-filled defaults here, touching
  // any one field would silently persist the rest as if the user had
  // explicitly chosen them too.
  const [criteria, setCriteria] = useState({ ...(user.predefined_plan_criteria || {}) });
  const [newWine, setNewWine] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  async function save(next) {
    setSaving(true); setError(""); setSaved(false);
    try {
      const updated = await api.updateMe(next);
      setSaved(true);
      onSaved && onSaved(updated.user);
      setTimeout(() => setSaved(false), 1500);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  const set = (k, v) => { const next = { ...prefs, [k]: v }; setPrefs(next); save({ [k]: v }); };
  // v === undefined (or "" for selects) means "back to ---" — delete the
  // key entirely rather than storing an empty/invalid value, so PlanPage's
  // own fallback defaults apply for that field instead of an empty string
  // that wouldn't match any of its option values.
  const setCriterion = (k, v) => {
    const next = { ...criteria };
    if (v === undefined || v === "") delete next[k];
    else next[k] = v;
    setCriteria(next);
    save({ predefined_plan_criteria: next });
  };

  function addWine() {
    const w = newWine.trim();
    if (!w) return;
    const next = [...prefs.favorite_wines, w];
    setPrefs(p => ({ ...p, favorite_wines: next }));
    setNewWine("");
    save({ favorite_wines: next });
  }
  function removeWine(i) {
    const next = prefs.favorite_wines.filter((_, idx) => idx !== i);
    setPrefs(p => ({ ...p, favorite_wines: next }));
    save({ favorite_wines: next });
  }

  const row = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: "1px solid #C4D0A8" };
  const label = { fontSize: 13, color: "#2C3820", fontWeight: 500 };
  const hint = { fontSize: 11, color: "#7A8E68", marginTop: 2 };

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <p style={{ fontSize: 10, fontWeight: 700, color: "#7A8E68", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>
        Notifications & preferences
      </p>

      <div style={row}>
        <div>
          <div style={label}>Notify me when a plan is ready</div>
        </div>
        <Toggle checked={prefs.notify_on_plan_completion} onChange={v => set("notify_on_plan_completion", v)} />
      </div>

      <div style={row}>
        <div>
          <div style={label}>Notify me on booking status changes</div>
        </div>
        <Toggle checked={prefs.notify_on_booking_status} onChange={v => set("notify_on_booking_status", v)} />
      </div>

      <div style={{ ...row, alignItems: "flex-start" }}>
        <div>
          <div style={label}>Preferred planning style</div>
          <div style={hint}>Form or conversational chat</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {["form", "chat"].map(m => (
            <button key={m} type="button" onClick={() => set("interaction_method", m)} style={{
              padding: "6px 12px", borderRadius: 100, fontSize: 12, textTransform: "capitalize", cursor: "pointer",
              border: prefs.interaction_method === m ? "1px solid #2E1E22" : "1px solid #C4D0A8",
              background: prefs.interaction_method === m ? "#E2D8DA" : "#EDF1E4",
              color: prefs.interaction_method === m ? "#886070" : "#5A6E48",
            }}>{m}</button>
          ))}
        </div>
      </div>

      <div style={{ padding: "12px 0 4px" }}>
        <div style={label}>Favorite wines</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {prefs.favorite_wines.map((w, i) => (
            <span key={i} style={{
              display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#886070",
              background: "rgba(107,39,55,0.08)", border: "1px solid rgba(107,39,55,0.15)",
              padding: "3px 6px 3px 10px", borderRadius: 100,
            }}>
              {w}
              <button onClick={() => removeWine(i)} style={{ background: "none", border: "none", color: "#886070", cursor: "pointer", display: "flex" }}>
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <input value={newWine} onChange={e => setNewWine(e.target.value)}
            onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addWine())}
            placeholder="e.g. Grüner Veltliner"
            style={{ flex: 1, padding: "8px 10px", borderRadius: 7, border: "1px solid #C4D0A8", background: "#EDF1E4", fontSize: 12, color: "#2C3820" }}
          />
          <button onClick={addWine} style={{ padding: "0 10px", borderRadius: 7, border: "1px solid #252525", background: "#EDF1E4", color: "#5A6E48", cursor: "pointer" }}>
            <Plus size={14} />
          </button>
        </div>
      </div>

      <div style={{ padding: "14px 0 4px", borderTop: "1px solid #C4D0A8", marginTop: 8 }}>
        <div style={label}>Default plan criteria</div>
        <div style={hint}>Prefills the plan form when you're signed in</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10 }}>
          <select value={criteria.road_slug ?? ""} onChange={e => setCriterion("road_slug", e.target.value)}
            style={{ padding: "8px 10px", borderRadius: 7, border: "1px solid #C4D0A8", background: "#EDF1E4", fontSize: 12, color: "#2C3820" }}>
            {ROAD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div style={{ display: "flex", gap: 8 }}>
            <label style={{ flex: 1, fontSize: 11, color: "#7A8E68" }}>
              Days
              <input type="number" min={1} max={7} placeholder="---" value={criteria.days ?? ""}
                onChange={e => setCriterion("days", e.target.value === "" ? undefined : Math.max(1, Math.min(7, parseInt(e.target.value) || 1)))}
                style={{ width: "100%", marginTop: 4, padding: "7px 10px", borderRadius: 7, border: "1px solid #C4D0A8", background: "#EDF1E4", fontSize: 12, color: "#2C3820", boxSizing: "border-box" }}
              />
            </label>
            <label style={{ flex: 1, fontSize: 11, color: "#7A8E68" }}>
              Travellers
              <input type="number" min={1} max={20} placeholder="---" value={criteria.party_size ?? ""}
                onChange={e => setCriterion("party_size", e.target.value === "" ? undefined : Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                style={{ width: "100%", marginTop: 4, padding: "7px 10px", borderRadius: 7, border: "1px solid #C4D0A8", background: "#EDF1E4", fontSize: 12, color: "#2C3820", boxSizing: "border-box" }}
              />
            </label>
          </div>
          <select value={criteria.budget_tier ?? ""} onChange={e => setCriterion("budget_tier", e.target.value === "" ? undefined : parseInt(e.target.value))}
            style={{ padding: "8px 10px", borderRadius: 7, border: "1px solid #C4D0A8", background: "#EDF1E4", fontSize: 12, color: "#2C3820" }}>
            {BUDGET_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={criteria.objective ?? ""} onChange={e => setCriterion("objective", e.target.value)}
            style={{ padding: "8px 10px", borderRadius: 7, border: "1px solid #C4D0A8", background: "#EDF1E4", fontSize: 12, color: "#2C3820" }}>
            {OBJECTIVE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 12, color: "#5A6E48" }}>Organic only by default</span>
            <div style={{ display: "flex", gap: 6 }}>
              {[["", "---"], [true, "Yes"], [false, "No"]].map(([val, lbl]) => {
                const isUnset = criteria.organic_only === undefined;
                const active = val === "" ? isUnset : criteria.organic_only === val;
                return (
                  <button key={lbl} type="button"
                    onClick={() => setCriterion("organic_only", val === "" ? undefined : val)}
                    style={{
                      padding: "5px 10px", borderRadius: 100, fontSize: 11, cursor: "pointer",
                      border: active ? "1px solid #2E1E22" : "1px solid #C4D0A8",
                      background: active ? "#E2D8DA" : "#EDF1E4",
                      color: active ? "#886070" : "#5A6E48",
                    }}>{lbl}</button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div style={{ height: 18, display: "flex", alignItems: "center", marginTop: 4 }}>
        {saving && <span style={{ fontSize: 11, color: "#7A8E68", display: "flex", alignItems: "center", gap: 5 }}><Loader size={11} /> Saving…</span>}
        {saved && !saving && <span style={{ fontSize: 11, color: "#4A7052" }}>Saved</span>}
        {error && <ErrorMsg message={error} />}
      </div>
    </div>
  );
}

function PrivacyControls({ user, logout }) {
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  async function handleExport() {
    setExporting(true); setError("");
    try {
      const data = await api.exportMyData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "wine4collect-my-data.json";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { setError(err.message); }
    finally { setExporting(false); }
  }

  async function handleDelete() {
    setDeleting(true); setError("");
    try {
      await api.deleteAccount();
      logout();
      navigate("/account");
    } catch (err) { setError(err.message); setDeleting(false); }
  }

  return (
    <div className="card flex flex-col gap-3" style={{ padding: "16px" }}>
      <p className="font-medium text-slate-dark text-sm" style={{ margin: 0 }}>Your data</p>
      <p className="text-xs text-stone" style={{ margin: 0 }}>
        Download a copy of your account, bookings and tour plans, or permanently delete your account.
        See our <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: "#886070" }}>Privacy Policy</a>.
      </p>

      {error && <ErrorMsg message={error} />}

      <button onClick={handleExport} disabled={exporting} className="btn-secondary w-full gap-2">
        {exporting ? <Loader size={14} /> : null} Export my data
      </button>

      {!confirming ? (
        <button onClick={() => setConfirming(true)} className="btn-secondary w-full gap-2" style={{ color: "#8B1A2F", borderColor: "rgba(139,26,47,0.3)" }}>
          Delete my account
        </button>
      ) : (
        <div style={{ background: "rgba(139,26,47,0.08)", border: "1px solid rgba(139,26,47,0.2)", borderRadius: 8, padding: 12 }}>
          <p style={{ fontSize: 12, color: "#8B1A2F", margin: "0 0 10px" }}>
            This permanently anonymizes your profile and removes your saved tour plans. This can't be undone. Are you sure?
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setConfirming(false)} className="btn-secondary" style={{ flex: 1 }}>Cancel</button>
            <button onClick={handleDelete} disabled={deleting} className="btn-secondary" style={{ flex: 1, background: "#8B1A2F", color: "#fff", borderColor: "#8B1A2F" }}>
              {deleting ? <Loader size={14} /> : null} Yes, delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Profile({ user, logout, onUserUpdated }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const roleInfo = ROLE_LABEL[user.role] || { label: user.role, color: "#5A6E48", bg: "#EAEFE2" };

  async function handleLogout() {
    setLoading(true);
    logout();
    navigate("/account");
  }

  return (
    <div className="px-4 pt-6 flex flex-col gap-4">
      <div className="card flex items-center gap-4">
        <div style={{
          width: 56, height: 56, borderRadius: "50%", background: "rgba(107,39,55,0.08)",
          border: "1px solid rgba(107,39,55,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <User size={24} color="#886070" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display font-semibold text-slate-dark truncate">
            {user.full_name || user.email}
          </p>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600,
            padding: "2px 9px", borderRadius: 100, marginTop: 4, background: roleInfo.bg, color: roleInfo.color,
          }}>
            {user.role === "winery_owner" ? <Wine size={10} /> : user.role === "admin" ? <Shield size={10} /> : null}
            {roleInfo.label}
          </span>
        </div>
      </div>

      {user.role === "winery_owner" && (
        <button onClick={() => navigate("/winery")} className="card-hover flex items-center gap-3">
          <div style={{ width: 40, height: 40, borderRadius: "50%", background: "rgba(122,106,85,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Wine size={18} color="#7A6A55" />
          </div>
          <div>
            <p className="font-medium text-slate-dark text-sm">Winery portal</p>
            <p className="text-xs text-stone">Manage profile, availability and bookings</p>
          </div>
        </button>
      )}

      {user.role === "traveler" && <BookingsSummaryCard />}

      {user.role === "traveler" && <TravelerSettings user={user} onSaved={onUserUpdated} />}

      <PrivacyControls user={user} logout={logout} />

      <button onClick={handleLogout} disabled={loading} className="btn-secondary w-full gap-2">
        <LogOut size={15} />
        {loading ? "Signing out…" : "Sign out"}
      </button>
    </div>
  );
}

export default function AccountPage() {
  const { user, loading, logout, refreshMe } = useAuth("traveler");
  const navigate = useNavigate();
  const [, forceRender] = useState(0);

  return (
    <div className="min-h-screen pb-20">
      <TopBar title="Account" />

      {loading ? (
        <div className="px-4 pt-6">
          <div className="card animate-pulse">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-chalk-warm" />
              <div className="flex-1">
                <div className="h-4 bg-chalk-warm rounded w-1/2 mb-2" />
                <div className="h-3 bg-chalk-warm rounded w-1/4" />
              </div>
            </div>
          </div>
        </div>
      ) : user ? (
        <Profile user={user} logout={logout} onUserUpdated={() => { refreshMe && refreshMe(); forceRender(x => x + 1); }} />
      ) : (
        <div className="px-4 pt-6">
          <div className="mb-6">
            <p className="section-eyebrow">Welcome</p>
            <h2 className="section-title">Sign in to save tours</h2>
            <p className="text-sm text-stone mt-1">
              Guest planning is always free. Create an account to save and revisit your tours, book tastings, and manage notifications.
            </p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button onClick={() => navigate("/tourist/login", { state: { from: "/account" } })} className="btn-primary">
              Sign in
            </button>
            <button onClick={() => navigate("/tourist/signup")} className="btn-secondary">
              Create account
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
